/**
 * Seats the flat `[node]` headings into the scene tree, and resolves a root-relative node path over
 * it. The parser, the linter and the language features all read this one seating. The headings'
 * nodes stay untouched: the tree holds a copy of each seated node, so a seating is repeatable.
 */
import { SCENE_ROOT_PATH, joinPath, parentPathOf, pathBetween, relativeTo, spelledNodePath, } from '../utils/nodePath.js';
import { hasForeignOwner, isInstanceBelowRoot, isOwnedByRoot, NO_OWNER, uniqueNameSetting, } from './nodeOwner.js';
import { UNIQUE_NODE_PREFIX, nodePathWalkNames, simplifiedNames, walkFromRoot, walkedPath, } from '../godot/string/nodePath.js';
import { reparentedName, reRootedName } from '../godot/scene/packedScene.js';
import { contentTrailSegment, idTrailEnd, ownerTrailEnd, trailHeadingsOf } from './idTrail.js';
import { refusesToInstantiate } from './instantiateRefusal.js';
import { isTypeUnknowable } from './typeUnknowable.js';
import { insertAtIndex } from '../godot/scene/siblingIndex.js';
export const MISSING = { status: 'missing' };
export const UNKNOWABLE = { status: 'unknowable' };
/**
 * Written only by {@link seatScene}, once per call, keyed by the roots array it returns. An entry
 * clears when nothing holds that array any more, and never goes stale, as nothing mutates a seated
 * tree. Only a scene with a root fallback keeps its heading nodes.
 */
// eslint-disable-next-line no-restricted-syntax -- seatScene writes each entry from outside, and sceneSeating only reads it back
const seatings = new WeakMap();
/**
 * Seats `origins`. The root claims the `%Name` of each {@link RootFallback} whose heading is not in
 * `ownersFound`, the headings whose `owner=` walk a reader that sees the instance found a node for.
 * Without `ownersFound`, which only such a reader passes, a walk through such a claim is unknowable.
 */
export function seatScene(origins, ownersFound) {
    const { rootFallbacks, ...seating } = origins.length === 0 ? noScene() : seatHeadings(origins, ownersFound);
    const { resolve, ownership, headings } = seating;
    seatings.set(seating.roots, {
        resolve,
        ownership,
        headings,
        rootFallbacks,
        ...(rootFallbacks.length > 0 ? { origins: origins } : {}),
    });
    return seating;
}
/**
 * The roots of the scene these roots were seated from, seated again with `ownersFound`
 * ({@link seatScene}). Only roots with a {@link RootFallback} can be.
 */
export function reseatedRoots(roots, ownersFound) {
    const { origins } = seatings.get(roots) ?? {};
    if (!origins)
        throw new Error(`expected roots with a root fallback, got ${roots.length} other root(s)`);
    return seatScene(origins, ownersFound).roots;
}
function noScene() {
    return {
        roots: [],
        seated: new Map(),
        pathOf: new Map(),
        resolve: () => MISSING,
        ownership: emptyOwnership(),
        headings: [],
        rootFallbacks: [],
    };
}
function seatHeadings(origins, ownersFound) {
    // Heading 0 is the root when nothing is parentless: `packed_scene.cpp:218-219` makes
    // `i == 0` the root, so no later heading is a root too. Returning the flat list as
    // roots would make every node reachable and silence `orphanedNodes`.
    const rootNode = (origins.find(({ node }) => !node.parent) ?? origins[0]).node;
    const tables = newTables(rootNode, ownersFound);
    const reRootsVanished = !refusesToInstantiate(origins);
    // One pass in declaration order, as Godot resolves: at heading `i`,
    // `get_node_or_null(np)` (`packed_scene.cpp:157-165`) sees only the headings above.
    // A `parent=` naming a later node vanishes and re-roots (`:208-215`). Forward order
    // also gives `children` the file's sibling order, the 2D paint order, and the
    // `%Name` claims the order instantiate sets each owner in (`:565-570`).
    const seatedByPath = new Set([rootNode]);
    const isPlaced = ({ node: heading }) => seatedByPath.has(heading);
    for (const [index, { node }] of origins.entries()) {
        if (node === rootNode)
            continue;
        // A second parentless node is orphaned here. `orphanedNodes` finds it absent from
        // `seated`, so it holds no second copy of the resolution rules.
        if (!node.parent)
            continue;
        // Only a heading that gets a seat resolves its `owner=`.
        const resolveOwner = () => node.owner === undefined ? undefined : seatedOwner(tables, origins, index, isPlaced);
        const end = walkFromRoot(node.parent, tables.tree);
        const parentPath = walkedPath(end);
        rememberUndecidedSeat(tables, node, typeof end === 'string' ? end : null);
        if (parentPath !== null && seatBelowParent(tables, node, parentPath, resolveOwner)) {
            seatedByPath.add(node);
            rememberTrailFallback(tables, origins, index, isPlaced);
            continue;
        }
        // An unlisted end passed content another file lists, where Godot may still find the
        // parent, so nothing here says the path vanished and it never re-roots.
        if (end !== null && typeof end !== 'string')
            continue;
        // The path has vanished. A `parent_id_path=` is tried first (`:161-163`). Where it lands in
        // this file, the node sits under that heading. Where it enters an instance, the node sits
        // somewhere this parse cannot name, so the instance's graft places it.
        if (!reRootsVanished)
            continue;
        const recovered = idTrailEnd(origins, index, isPlaced);
        if (recovered === undefined)
            reRoot(tables, node, resolveOwner);
        else if (recovered.kind === 'lands')
            seatLanded(tables, node, origins[recovered.heading].node, resolveOwner);
        else
            seatInContent(tables, node, origins[recovered.heading].node, recovered.rest, resolveOwner);
    }
    return {
        roots: [tables.root],
        seated: tables.seated,
        pathOf: dropContentSeatPaths(tables),
        resolve: seatedResolver(tables),
        ownership: tables.ownership,
        headings: trailHeadingsOf(origins),
        rootFallbacks: tables.rootFallbacks,
    };
}
/** One shared array, so a lookup keyed on the headings' identity builds once for every unseated scene. */
const NO_HEADINGS = [];
/**
 * The trail headings of the scene {@link seatScene} built these roots from, or none for roots that
 * came from elsewhere, such as a GLB's synthesised scene, which has no headings to follow.
 */
export function sceneHeadings(roots) {
    return seatings.get(roots)?.headings ?? NO_HEADINGS;
}
/** The root fallbacks of the scene {@link seatScene} built these roots from, or none for roots that came from elsewhere. */
export function sceneRootFallbacks(roots) {
    return seatings.get(roots)?.rootFallbacks ?? [];
}
/**
 * The seating {@link seatScene} built for these roots. Every tree a consumer reads comes from a
 * parse, so roots from anywhere else are a caller's error.
 */
export function sceneSeating(roots) {
    const seating = seatings.get(roots);
    if (!seating)
        throw new Error(`expected roots that seatScene returned, got ${roots.length} other root(s)`);
    return seating;
}
/**
 * Seats one heading under the node at the path its `parent=` walks to, else below the instance
 * enclosing that path, whose sub-scene or GLB the path descends into. False where the path names
 * no node of this file. The map IS the child lookup the walk asks at every descent, so `./Mid`, `Mid/` and `Mid`
 * name the one node Godot resolves them all to while `Missing/../Mid` names none.
 */
function seatBelowParent(tables, node, parentPath, resolveOwner) {
    const parentNode = tables.byPath.get(parentPath);
    if (parentNode) {
        const seat = seatCopy(tables, node, resolveOwner);
        // An instance's own nodes are not in this list, so an `index=` below one is no place in it.
        if (parentNode.instance)
            parentNode.children.push(seat);
        else
            insertAtIndex(parentNode.children, seat);
        register(tables, parentPath, seat, parentNode);
        return true;
    }
    // A path into instanced content names nodes in the sub-scene or GLB, so the
    // nearest enclosing instance anchors it and the content matches the rest.
    const anchor = findInstanceAnchor(parentPath, tables);
    if (!anchor)
        return false;
    const seat = seatCopy(tables, node, resolveOwner);
    seat.instanceSubPath = anchor.subPath;
    anchor.node.children.push(seat);
    register(tables, anchor.foldedParentPath, seat, anchor.node);
    return true;
}
/**
 * Seats a heading under the heading its `parent_id_path=` landed on, at the path that heading's
 * node has in the tree (`packed_scene.cpp:161-163`, `:1985-2005`). A heading with no name has no
 * path to continue from, and this parse leaves the node out.
 */
function seatLanded(tables, node, landedOn, resolveOwner) {
    const parentPath = tables.pathOf.get(tables.seated.get(landedOn));
    if (parentPath === undefined || !seatBelowParent(tables, node, parentPath, resolveOwner))
        return;
    tables.seated.get(node).landedByIdTrail = true;
}
/**
 * Keeps on the seat of a heading below an instance's content where its `parent_id_path=` would seat
 * it, should the content lack the path its `parent=` names (`packed_scene.cpp:161-163`). Only the
 * live tree can tell, so the seat holds both.
 */
function rememberTrailFallback(tables, origins, index, isPlaced) {
    if (origins[index].idTrail === undefined)
        return;
    const seat = tables.seated.get(origins[index].node);
    if (seat.instanceSubPath === undefined)
        return;
    const end = idTrailEnd(origins, index, isPlaced);
    if (end === undefined)
        return;
    const target = tables.seated.get(origins[end.heading].node);
    const path = target && fileSeatPath(tables, target);
    if (path === undefined)
        return;
    seat.parentTrailFallback = end.kind === 'lands' ? { path } : { path, contentTrail: end.rest };
}
/**
 * Seats a heading below the instance heading its `parent_id_path=` enters, with the rest of the
 * trail for that instance's graft (`packed_scene.cpp:1985-2005`). The node it finds lies in the
 * instance's content, so its key holds a segment no heading writes (`registerInContent`). A heading with no seat leaves the node out.
 */
function seatInContent(tables, node, instance, rest, resolveOwner) {
    const instanceSeat = tables.seated.get(instance);
    if (!instanceSeat)
        return;
    const seat = seatCopy(tables, node, resolveOwner);
    seat.landedByIdTrail = true;
    seat.parentContentTrail = rest;
    instanceSeat.children.push(seat);
    registerInContent(tables, instanceSeat, seat, rest);
}
/** Keys `node` at `path` for the headings that name it and the children that hang below it. */
function keyAt(tables, path, node) {
    tables.byPath.set(path, node);
    tables.pathOf.set(node, path);
}
/**
 * Keys a node seated below an instance for a trail, so a later heading names it by its `%Name`
 * (`node.cpp:1930-1938`, `packed_scene.cpp:565-570`) and its children follow it to the graft's
 * placement. Its path in the tree is the graft's to find, so the key holds a segment no heading
 * writes: `:` ends a name in a NodePath. A claim another owner holds is the graft's to land too.
 */
function registerInContent(tables, instanceSeat, seat, rest) {
    tables.contentSeats.add(seat);
    if (!seat.name)
        return;
    const segment = contentTrailSegment(rest);
    const { path: authored, livePath } = tables.places.get(instanceSeat);
    const place = placeBelow({ path: joinPath(authored, segment), livePath: joinPath(livePath, segment) }, seat);
    tables.places.set(seat, place);
    const path = joinPath(joinPath(tables.pathOf.get(instanceSeat), segment), seat.name);
    keyAt(tables, path, seat);
    recordSetting(tables, { node: seat, ...place }, path, isOwnedByRoot(seat, true));
}
/**
 * The path `seat` holds in this file, or undefined for a node seated for a trail: only the graft knows
 * its path in the tree, so no root-relative path an owner or a trail names reaches it.
 */
function fileSeatPath(tables, seat) {
    return tables.contentSeats.has(seat) ? undefined : tables.pathOf.get(seat);
}
/** Removes from `pathOf` the nodes whose path only the graft knows, and returns it. Called once, when the seating is done. */
function dropContentSeatPaths({ pathOf, contentSeats }) {
    for (const seat of contentSeats)
        pathOf.delete(seat);
    return pathOf;
}
/**
 * Re-parents a node whose `parent=` vanished to the scene root and renames it
 * `<path>#<name>` (`packed_scene.cpp:208-215`, `:561-563`), numbered past a root child that
 * holds the name (`set_name`, `node.cpp:1450`, `:1593-1640`). The authored name stays on `authoredName`.
 */
function reRoot(tables, node, resolveOwner) {
    const seat = seatCopy(tables, node, resolveOwner);
    seat.authoredName = node.name;
    seat.name = reRootedName(node.parent, node.name, tables.rootChildNames);
    insertAtIndex(tables.root.children, seat);
    register(tables, SCENE_ROOT_PATH, seat, tables.root);
}
/**
 * The tree's own copy of a heading's node, childless and with its `owner=` as `seatedOwner` resolved
 * it, so the seating never writes to the heading.
 */
function seatCopy(tables, node, resolveOwner) {
    const owner = resolveOwner();
    const seat = { ...node, children: [], ...owner };
    tables.seated.set(node, seat);
    return seat;
}
/**
 * Where a heading below an undecided `%Name` may sit: under this file's claimant, and re-rooted.
 * `parentPath` is its parent's path where the walk found one, or null where it ended elsewhere.
 */
function rememberUndecidedSeat(tables, { parent, name }, parentPath) {
    if (tables.undecidedKeys.size === 0 || !parent.includes(UNIQUE_NODE_PREFIX))
        return;
    const names = nodePathWalkNames(parent);
    const at = names.findIndex((part) => tables.undecidedKeys.has(part));
    if (at === -1)
        return;
    // An inherited root's walk ends unlisted at the claim, so the claimant's own path continues it.
    const claimedParent = parentPath ??
        walkedPath(walkFromRoot([tables.uniquePaths.get(names[at]), ...names.slice(at + 1)].join('/'), tables.tree));
    if (claimedParent !== null)
        tables.undecidedSeats.add(joinPath(claimedParent, name));
    tables.undecidedSeats.add(reparentedName(parent, name));
}
/**
 * The node a path names over the seated tables. A walk Godot ends in null is missing. One that
 * passes through content this file does not list, onto no heading, or through an undecided
 * `%Name` or seat, is unknowable. The walk keeps only the tables it reads, as the seating cache
 * holds it as long as the tree lives.
 */
function seatedResolver({ byPath, tree, undecidedKeys, undecidedSeats, }) {
    return (path) => {
        if (undecidedKeys.size > 0 && isUndecided(path, undecidedKeys, undecidedSeats))
            return UNKNOWABLE;
        const end = walkFromRoot(path, tree);
        if (end === null)
            return MISSING;
        const node = typeof end === 'string' ? byPath.get(end) : undefined;
        return node ? { status: 'found', node } : UNKNOWABLE;
    };
}
/** Whether `path` passes through an undecided `%Name`, or names an undecided seat or a node below one. */
function isUndecided(path, keys, seats) {
    // The walk's names, not a split on `/`: a `:subname` stays off the last name (node_path.cpp:405-427).
    const names = nodePathWalkNames(path);
    if (names.some((name) => keys.has(name)))
        return true;
    let prefix = '';
    for (const name of simplifiedNames(names)) {
        prefix = joinPath(prefix, name);
        if (seats.has(prefix))
            return true;
    }
    return false;
}
/** Tables holding only the scene root's copy, which sits at the empty path. */
function newTables(rootNode, ownersFound) {
    const root = { ...rootNode, children: [] };
    const byPath = new Map([[SCENE_ROOT_PATH, root]]);
    // The root claims no `%Name`: `set_unique_name_in_owner` registers in the
    // node's OWNER (node.cpp:2222-2233) and the scene root has none.
    const uniquePaths = new Map();
    const isOpaque = (path) => isOpaqueAt(byPath, path);
    return {
        root,
        seated: new Map([[rootNode, root]]),
        ownersFound,
        rootFallbacks: [],
        undecidedKeys: new Set(),
        undecidedSeats: new Set(),
        rootChildNames: new Set(),
        byPath,
        pathOf: new Map([[root, SCENE_ROOT_PATH]]),
        uniquePaths,
        places: new Map([[root, { path: root.name, livePath: root.name }]]),
        enclosingInstances: new Map(),
        instanceNameSettings: new Map(),
        contentSeats: new Set(),
        ownership: emptyOwnership(),
        tree: { exists: (path) => canNameNode(byPath, path, isOpaque), isOpaque, uniquePaths },
    };
}
/**
 * Whether the node at a path keeps children or `%Name` claims another file lists: a missing path,
 * which passed the walk under such a node already, an override heading or instance
 * (`isTypeUnknowable`, since an override's defaulted `'Node'` is no real type), or a node another
 * owner holds.
 */
function isOpaqueAt(pathMap, path) {
    const node = pathMap.get(path);
    return node === undefined || isTypeUnknowable(node) || hasForeignOwner(node);
}
/**
 * Whether a name the walk descended onto could be a node: the child lookup at
 * `node.cpp:1941-1946`, answered by a parse blind to instanced scenes. Only a name
 * below a parent this file describes is one Godot fails to find.
 */
function canNameNode(pathMap, path, isOpaque) {
    if (pathMap.has(path))
        return true;
    return isOpaque(parentPathOf(path) ?? SCENE_ROOT_PATH);
}
/**
 * Key a seated node by the path its own children address it at, and record its `%Name` setting in
 * its owner's table. An override inside an instance keeps the sub-scene's owner, and an explicit
 * `owner=` names its own (`isOwnedByRoot`).
 */
function register(tables, parentPath, node, parent) {
    const place = placeBelow(tables.places.get(parent), node);
    tables.places.set(node, place);
    const instance = isInstanceBelowRoot(parent, parent === tables.root)
        ? parent
        : tables.enclosingInstances.get(parent);
    if (instance !== undefined)
        tables.enclosingInstances.set(node, instance);
    // A nameless heading claims no key: an empty name joins to its parent's own key and live path.
    // Godot seats it and places later siblings by path (`packed_scene.cpp:208-215`).
    if (!node.name)
        return;
    const isBelowContent = tables.contentSeats.has(parent);
    if (isBelowContent)
        tables.contentSeats.add(node);
    // Content sits in an instance, so an override heading below a trail seat keeps the sub-scene's owner.
    const isRootOwned = isOwnedByRoot(node, instance !== undefined || isBelowContent);
    if (instance !== undefined)
        recordInsideInstance(tables, place.livePath, node, isRootOwned);
    const path = joinPath(parentPath, node.name);
    if (parentPath === SCENE_ROOT_PATH)
        tables.rootChildNames.add(node.name);
    keyAt(tables, path, node);
    recordSetting(tables, { node, ...place }, path, isRootOwned, isBelowContent ? undefined : instance);
}
/**
 * Record the `%Name` setting of a seated node in its owner's table: the root's where `isRootOwned`,
 * else the one its explicit `owner=` names, else the one of `instance`, the instance it sits in.
 * An inherited root owns its base's nodes (packed_scene.cpp:227-231), so only its graft over the
 * base composes the one table both files' names land in, each at its heading.
 */
function recordSetting(tables, claim, path, isRootOwned, instance) {
    const setting = uniqueNameSetting(claim.node);
    if (setting === undefined)
        return;
    if (isRootOwned) {
        if (setting)
            claimInRoot(tables, claim, path);
        if (tables.root.instance)
            handToInstance(tables, tables.root, {
                pathInInstance: relativeTo(tables.root.name, claim.livePath),
                isUnique: setting,
                ownerInInstance: '',
            });
        return;
    }
    if (claim.node.owner !== undefined) {
        handToOwner(tables, claim, path, setting);
        return;
    }
    if (instance === undefined)
        return;
    handToInstance(tables, instance, {
        pathInInstance: pathInInstance(tables, instance, claim.livePath),
        isUnique: setting,
    });
}
const NO_SEATED_OWNER = { owner: NO_OWNER };
/**
 * The `owner=` of `origins[index]` as the path of the node it names, walked from the root over the
 * headings seated so far, as instantiate walks it when it sets the owner (packed_scene.cpp:159-168,
 * :565-568): `.` for the root, and a `%Name` replaced by its claimant. A walk into content this file
 * does not list keeps the rest for that instance to resolve. A walk that finds nothing falls back to
 * the `owner_uid_path=`, and where that finds nothing the node has no owner.
 */
function seatedOwner(tables, origins, index, isPlaced) {
    const walked = walkedPath(walkFromRoot(origins[index].node.owner, tables.tree));
    if (walked === null)
        return recoveredOwner(tables, origins, index, isPlaced) ?? NO_SEATED_OWNER;
    const owner = spelledNodePath(walked);
    // A path no heading holds lies in an instance's content, where the walk may still find nothing.
    const fallback = tables.byPath.has(walked) ? undefined : recoveredOwner(tables, origins, index, isPlaced);
    return fallback ? { owner, ownerTrailFallback: fallback } : { owner };
}
/**
 * The owner the `owner_uid_path=` of `origins[index]` finds: the heading it lands on, or the instance
 * heading it enters with the rest of the trail. Undefined where it finds nothing this parse can name.
 */
function recoveredOwner(tables, origins, index, isPlaced) {
    const recovered = ownerTrailEnd(origins, index, isPlaced);
    if (recovered === undefined)
        return undefined;
    const seat = tables.seated.get(origins[recovered.heading].node);
    const path = seat && fileSeatPath(tables, seat);
    if (path === undefined)
        return undefined;
    const owner = spelledNodePath(path);
    return recovered.kind === 'lands' ? { owner } : { owner, ownerContentTrail: recovered.rest };
}
/**
 * Hand a setting to the instance that holds the node an explicit `owner=` names. The loader sets
 * that owner (resource_format_text.cpp:257-262) and the name registers on it (packed_scene.cpp:565-570),
 * so a node outside that instance is spelt with `..`. An owner no instance holds names a table no
 * instance reads.
 */
function handToOwner(tables, claim, seatPath, isUnique) {
    const { owner: ownerPath, ownerContentTrail, ownerTrailFallback } = claim.node;
    if (ownerPath === undefined || ownerPath === NO_OWNER)
        return;
    const owner = instanceHolding(tables, rootRelative(ownerPath));
    if (!owner)
        return;
    const pathIn = (instance) => pathBetween(tables.places.get(instance).livePath, claim.livePath);
    const setting = {
        pathInInstance: pathIn(owner.instance),
        isUnique,
        ...asOwnerFallback(owner.pathInInstance, { ownerContentTrail }),
    };
    if (!ownerTrailFallback) {
        handToInstance(tables, owner.instance, setting);
        return;
    }
    if (isPlainRoot(tables, ownerTrailFallback)) {
        claimOnFallback(tables, owner.instance, setting, claim, seatPath);
        return;
    }
    const fallback = instanceHolding(tables, rootRelative(ownerTrailFallback.owner));
    if (!fallback) {
        handToInstance(tables, owner.instance, setting);
        return;
    }
    const ownerFallback = asOwnerFallback(fallback.pathInInstance, ownerTrailFallback);
    if (fallback.instance === owner.instance) {
        handToInstance(tables, owner.instance, { ...setting, ownerFallback });
        return;
    }
    // Only the graft of the walked owner's instance knows whether the walk finds a node.
    const handOff = Symbol(claim.livePath);
    handToInstance(tables, owner.instance, { ...setting, ownerHandOff: handOff });
    handToInstance(tables, fallback.instance, {
        pathInInstance: pathIn(fallback.instance),
        isUnique,
        ...ownerFallback,
        awaits: handOff,
    });
}
/** Whether an `owner_uid_path=` owner is the root of a file that inherits no base, whose table is this seating's. */
function isPlainRoot(tables, { owner, ownerContentTrail }) {
    return rootRelative(owner) === SCENE_ROOT_PATH && ownerContentTrail === undefined && !tables.root.instance;
}
/**
 * Hand a setting to the instance its `owner=` walk enters, and claim its name in the root, which
 * its `owner_uid_path=` names, where the walk finds no node there ({@link RootFallback}). A seating
 * that knows the walk finds one hands the setting only.
 */
function claimOnFallback(tables, instance, setting, claim, seatPath) {
    const heading = headingOf(tables, claim.node);
    if (tables.ownersFound?.has(heading)) {
        handToInstance(tables, instance, setting);
        return;
    }
    const handOff = Symbol(claim.livePath);
    handToInstance(tables, instance, { ...setting, ownerHandOff: handOff });
    tables.rootFallbacks.push({ heading, handOff });
    if (!setting.isUnique)
        return;
    const key = UNIQUE_NODE_PREFIX + claim.node.name;
    claimInRoot(tables, claim, seatPath);
    if (!tables.ownersFound && tables.uniquePaths.get(key) === seatPath)
        tables.undecidedKeys.add(key);
}
/** The heading whose node `seat` is a copy of. A scan, as only a rare root fallback asks. */
function headingOf({ seated }, seat) {
    for (const [heading, copy] of seated)
        if (copy === seat)
            return heading;
    throw new Error(`expected a seated copy, got "${seat.name}"`);
}
/** The owner an `owner_uid_path=` finds, as the instance at whose path it sits reads it. */
function asOwnerFallback(ownerInInstance, { ownerContentTrail }) {
    return { ownerInInstance, ...(ownerContentTrail === undefined ? {} : { ownerContentTrail }) };
}
/** The root-relative spelling of an owner path: `.` is the root. */
function rootRelative(ownerPath) {
    return ownerPath === '.' ? SCENE_ROOT_PATH : ownerPath;
}
/**
 * The nearest seated instance at or above the node `path` names, with that node's path below it,
 * or undefined where no instance holds it. The rest of a path past its last heading lies in the
 * content of the instance that heading is in.
 */
function instanceHolding(tables, path) {
    const names = path.split('/');
    for (let end = names.length; end >= 0; end--) {
        const seated = tables.byPath.get(names.slice(0, end).join('/'));
        if (!seated)
            continue;
        const instance = isInstanceAt(tables, seated) ? seated : tables.enclosingInstances.get(seated);
        if (!instance)
            return undefined;
        const rest = names.slice(end).join('/');
        const seatedLivePath = tables.places.get(seated).livePath;
        const ownerLivePath = rest ? joinPath(seatedLivePath, rest) : seatedLivePath;
        return { instance, pathInInstance: pathInInstance(tables, instance, ownerLivePath) };
    }
    return undefined;
}
/**
 * Whether a seated node is an instance whose graft composes a table: one below the root, or an
 * inherited root, which owns its base's nodes (packed_scene.cpp:227-231).
 */
function isInstanceAt(tables, node) {
    return node === tables.root ? Boolean(node.instance) : isInstanceBelowRoot(node, false);
}
/** Append a setting to the instance node it sits in, in file order, after the children seated so far. */
function handToInstance(tables, instance, setting) {
    let settings = tables.instanceNameSettings.get(instance);
    if (!settings) {
        settings = [];
        tables.instanceNameSettings.set(instance, settings);
        instance.instanceNameSettings = settings;
    }
    settings.push({ ...setting, hostChildrenBefore: instance.children.length });
}
/** The live path `livePath` below the seated `instance`, or undefined where it is not at or below it. */
function pathInInstance(tables, instance, livePath) {
    return relativeTo(tables.places.get(instance).livePath, livePath);
}
/** Fold a heading inside an instance into what the file's earlier headings at its live path say. */
function recordInsideInstance(tables, livePath, node, isRootOwned) {
    const earlier = tables.ownership.insideInstances.get(livePath);
    tables.ownership.insideInstances.set(livePath, {
        isRootOwned: isRootOwned || earlier?.isRootOwned === true,
        isAdded: !node.overridesExistingNode || earlier?.isAdded === true,
    });
}
/** The ownership record of a file that has seated nothing yet. */
function emptyOwnership() {
    return { claims: new Map(), insideInstances: new Map() };
}
/**
 * A child's place below its parent's. The two spellings diverge only at a node whose `parent=`
 * descends into instanced content, which alone carries `instanceSubPath`.
 */
function placeBelow(parent, node) {
    if (!node.name)
        return parent;
    const under = node.instanceSubPath ? joinPath(parent.livePath, node.instanceSubPath) : parent.livePath;
    return { path: joinPath(parent.path, node.name), livePath: joinPath(under, node.name) };
}
/**
 * Records a claim in the root's table and in the walk's view of it, unless an earlier node holds
 * the name: `_acquire_unique_name_in_owner` refuses to overwrite an entry (`node.cpp:2225-2231`).
 */
function claimInRoot(tables, claim, seatPath) {
    const key = UNIQUE_NODE_PREFIX + claim.node.name;
    if (tables.uniquePaths.has(key))
        return;
    tables.uniquePaths.set(key, seatPath);
    tables.ownership.claims.set(key, claim);
    // The base of an inherited root may claim the name first (node.cpp:2222-2233).
    if (tables.root.instance)
        tables.undecidedKeys.add(key);
}
/**
 * The nearest instance node enclosing a resolved parent path, and the path below it,
 * or `null` for a malformed path. Longest prefix first, so nested instances anchor at
 * the innermost one: the scene that resolves the sub-path.
 */
function findInstanceAnchor(parentPath, tables) {
    // The root is seated from the start, so a node naming it never reaches here.
    if (!parentPath)
        return null;
    const segments = parentPath.split('/');
    // Start one short of the full path: had the whole thing resolved, ordinary
    // placement would already have used it.
    for (let depth = segments.length - 1; depth >= 0; depth--) {
        const candidate = tables.byPath.get(segments.slice(0, depth).join('/'));
        if (candidate?.instance) {
            return {
                node: candidate,
                subPath: segments.slice(depth).join('/'),
                foldedParentPath: parentPath,
            };
        }
    }
    return null;
}
/**
 * The headings whose `parent=` names nothing, derived from the seating rather than a second copy
 * of its rules: the nodes it re-rooted, and those it never seated, which are a vanished path in a
 * file or on a heading Godot does not re-root, or a non-root with no `parent=`
 * (`packed_scene.cpp:207`).
 */
export function orphanedNodes(all, seating) {
    // Each node arrives with its line: a lookup would need a fallback, and line 0 reads
    // as a real location in an editor gutter.
    const isSeated = isSeatedAtOwnPathIn(seating);
    return all.filter((heading) => !isSeated(heading));
}
/**
 * Whether a heading's node sits at the path its `parent=` names in `seating`. A re-rooted copy keeps
 * its `authoredName`, a landed one lives under the heading its trail named, and a node the seating
 * left out has no copy.
 */
export function isSeatedAtOwnPathIn({ seated, }) {
    return ({ node }) => {
        const seat = seated.get(node);
        return seat !== undefined && !isReRooted(seat) && seat.landedByIdTrail === undefined;
    };
}
/** Whether the tree node is a copy the builder re-rooted, which keeps the name its heading wrote. */
export function isReRooted(seat) {
    return seat.authoredName !== undefined;
}
/**
 * The seated node each seated copy sits under because its `parent=` names it, or its
 * `parent_id_path=` landed it there. A re-rooted copy has none, since its path vanished, and a copy
 * seated below an instance for a path into that instance has none, since no heading of this file is
 * the node the path names.
 */
export function seatedParents(roots) {
    const parents = new Map();
    const pending = [...roots];
    for (let parent = pending.pop(); parent; parent = pending.pop()) {
        for (const child of parent.children) {
            if (!isReRooted(child) && child.instanceSubPath === undefined && child.parentContentTrail === undefined)
                parents.set(child, parent);
            pending.push(child);
        }
    }
    return parents;
}
//# sourceMappingURL=sceneTreeBuilder.js.map